import React, { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";
import type { BMICaptureNode as BMICaptureNodeType } from "@/lib/pharmacyFirst/types";
import { mapEthnicityToAdjustmentGroup } from "@/lib/pharmacyFirst/pathways/aht/weight-selection.pathway";

export interface BMICaptureAnswer {
  heightCm: number;
  weightKg: number;
  bmi: number;
}

interface BMICaptureNodeProps {
  node: BMICaptureNodeType;
  isDarkMode?: boolean;
  stepNumber?: number;
  onAnswer: (answer: BMICaptureAnswer) => void;
  patientEthnicAdjustmentGroup?: string;
  existingHeightCm?: number | null;
  existingWeightKg?: number | null;
}

type HeightUnit = "cm" | "ft/in";
type WeightUnit = "kg" | "st/lbs";

const ADJUSTED_GROUPS = [
  "south_asian",
  "chinese",
  "other_asian",
  "middle_eastern",
  "black_african",
  "african_caribbean",
];

function cmToFtIn(cm: number): { feet: number; inches: number } {
  const totalInches = cm / 2.54;
  const feet = Math.floor(totalInches / 12);
  const inches = Math.round((totalInches % 12) * 10) / 10;
  return { feet, inches };
}

function ftInToCm(feet: number, inches: number): number {
  return (feet * 12 + inches) * 2.54;
}

function kgToStLbs(kg: number): { stone: number; pounds: number } {
  const totalLbs = kg * 2.20462;
  const stone = Math.floor(totalLbs / 14);
  const pounds = Math.round((totalLbs % 14) * 10) / 10;
  return { stone, pounds };
}

function stLbsToKg(stone: number, pounds: number): number {
  return (stone * 14 + pounds) / 2.20462;
}

function calculateBMI(heightCm: number, weightKg: number): number | null {
  if (heightCm <= 0 || weightKg <= 0) return null;
  const heightM = heightCm / 100;
  return Math.round((weightKg / (heightM * heightM)) * 10) / 10;
}

export function BMICaptureNode({
  node,
  isDarkMode = false,
  stepNumber,
  onAnswer,
  patientEthnicAdjustmentGroup,
  existingHeightCm,
  existingWeightKg,
}: BMICaptureNodeProps) {
  const [heightUnit, setHeightUnit] = useState<HeightUnit>("cm");
  const [weightUnit, setWeightUnit] = useState<WeightUnit>("kg");

  const [heightCmText, setHeightCmText] = useState("");
  const [heightFeet, setHeightFeet] = useState("");
  const [heightInches, setHeightInches] = useState("");

  const [weightKgText, setWeightKgText] = useState("");
  const [weightStone, setWeightStone] = useState("");
  const [weightPounds, setWeightPounds] = useState("");

  const [error, setError] = useState<string | null>(null);

  const resolvedEthnicGroup = patientEthnicAdjustmentGroup
    ? (ADJUSTED_GROUPS.includes(patientEthnicAdjustmentGroup)
        ? patientEthnicAdjustmentGroup
        : mapEthnicityToAdjustmentGroup(patientEthnicAdjustmentGroup))
    : undefined;
  const isAdjusted = !!resolvedEthnicGroup && ADJUSTED_GROUPS.includes(resolvedEthnicGroup);

  useEffect(() => {
    if (existingHeightCm && existingHeightCm > 0) {
      setHeightCmText(String(existingHeightCm));
    } else {
      setHeightCmText("");
    }
    setHeightFeet("");
    setHeightInches("");
    if (existingWeightKg && existingWeightKg > 0) {
      setWeightKgText(String(existingWeightKg));
    } else {
      setWeightKgText("");
    }
    setWeightStone("");
    setWeightPounds("");
    setHeightUnit("cm");
    setWeightUnit("kg");
    setError(null);
  }, [node.id, existingHeightCm, existingWeightKg]);

  const resolvedHeightCm = useMemo(() => {
    if (heightUnit === "cm") {
      const v = parseFloat(heightCmText);
      return Number.isFinite(v) && v > 0 ? v : null;
    }
    const ft = parseFloat(heightFeet) || 0;
    const inch = parseFloat(heightInches) || 0;
    if (ft <= 0 && inch <= 0) return null;
    return Math.round(ftInToCm(ft, inch) * 10) / 10;
  }, [heightUnit, heightCmText, heightFeet, heightInches]);

  const resolvedWeightKg = useMemo(() => {
    if (weightUnit === "kg") {
      const v = parseFloat(weightKgText);
      return Number.isFinite(v) && v > 0 ? v : null;
    }
    const st = parseFloat(weightStone) || 0;
    const lbs = parseFloat(weightPounds) || 0;
    if (st <= 0 && lbs <= 0) return null;
    return Math.round(stLbsToKg(st, lbs) * 10) / 10;
  }, [weightUnit, weightKgText, weightStone, weightPounds]);

  const bmi =
    resolvedHeightCm && resolvedWeightKg
      ? calculateBMI(resolvedHeightCm, resolvedWeightKg)
      : null;

  const handleHeightUnitSwitch = (unit: HeightUnit) => {
    if (unit === heightUnit) return;
    if (resolvedHeightCm && unit === "ft/in") {
      const { feet, inches } = cmToFtIn(resolvedHeightCm);
      setHeightFeet(String(feet));
      setHeightInches(String(inches));
    } else if (unit === "cm" && (heightFeet || heightInches)) {
      const ft = parseFloat(heightFeet) || 0;
      const inch = parseFloat(heightInches) || 0;
      const cm = ftInToCm(ft, inch);
      setHeightCmText(String(Math.round(cm * 10) / 10));
    }
    setHeightUnit(unit);
  };

  const handleWeightUnitSwitch = (unit: WeightUnit) => {
    if (unit === weightUnit) return;
    if (resolvedWeightKg && unit === "st/lbs") {
      const { stone, pounds } = kgToStLbs(resolvedWeightKg);
      setWeightStone(String(stone));
      setWeightPounds(String(pounds));
    } else if (unit === "kg" && (weightStone || weightPounds)) {
      const st = parseFloat(weightStone) || 0;
      const lbs = parseFloat(weightPounds) || 0;
      const kg = stLbsToKg(st, lbs);
      setWeightKgText(String(Math.round(kg * 10) / 10));
    }
    setWeightUnit(unit);
  };

  const validate = (): string | null => {
    if (!resolvedHeightCm) return "Please enter a valid height.";
    if (!resolvedWeightKg) return "Please enter a valid weight.";
    const hCfg = node.heightConfig;
    if (resolvedHeightCm < hCfg.hardMin || resolvedHeightCm > hCfg.hardMax) {
      return `Height must be between ${hCfg.hardMin} and ${hCfg.hardMax} cm.`;
    }
    const wCfg = node.weightConfig;
    if (resolvedWeightKg < wCfg.hardMin || resolvedWeightKg > wCfg.hardMax) {
      return `Weight must be between ${wCfg.hardMin} and ${wCfg.hardMax} kg.`;
    }
    return null;
  };

  const handleContinue = () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    onAnswer({
      heightCm: resolvedHeightCm!,
      weightKg: resolvedWeightKg!,
      bmi: bmi!,
    });
  };

  const pillBase = `px-3 py-1 rounded-full text-sm font-medium border transition-colors`;
  const pillActive = isDarkMode
    ? "bg-white text-black border-white"
    : "bg-gray-900 text-white border-gray-900";
  const pillInactive = isDarkMode
    ? "bg-transparent text-gray-400 border-gray-600 hover:border-gray-400"
    : "bg-transparent text-gray-600 border-gray-300 hover:border-gray-500";
  const labelClass = `text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"}`;
  const inputClass = `w-full ${isDarkMode ? "bg-gray-700 border-gray-600 text-white placeholder:text-gray-400" : "!bg-white !border-gray-300 !text-gray-900 placeholder:!text-gray-500"}`;

  return (
    <div
      className={`border rounded-2xl p-4 ${
        isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"
      }`}
    >
      <div className="flex items-center gap-3 mb-4">
        {stepNumber !== undefined && (
          <div
            className={`flex items-center justify-center w-6 h-6 rounded-full text-sm font-semibold flex-shrink-0 ${
              isDarkMode ? "bg-white text-black" : "bg-black text-white"
            }`}
          >
            {stepNumber}
          </div>
        )}
        <div className="flex-1">
          <h3
            className={`text-sm font-semibold ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            {node.text}
          </h3>
          {node.hint && (
            <p
              className={`text-xs mt-1 ${
                isDarkMode ? "text-gray-400" : "text-gray-600"
              }`}
            >
              {node.hint}
            </p>
          )}
        </div>
      </div>

      {/* Height */}
      <div className="space-y-2 mb-4">
        <div className="flex items-center justify-between">
          <Label className={labelClass}>Height</Label>
          <div className="flex gap-1">
            {(["cm", "ft/in"] as const).map((u) => (
              <button
                key={u}
                type="button"
                onClick={() => handleHeightUnitSwitch(u)}
                className={`${pillBase} ${heightUnit === u ? pillActive : pillInactive}`}
              >
                {u}
              </button>
            ))}
          </div>
        </div>
        {heightUnit === "cm" ? (
          <Input
            type="number"
            inputMode="decimal"
            step="0.1"
            value={heightCmText}
            onChange={(e) => {
              setHeightCmText(e.target.value);
              setError(null);
            }}
            placeholder="e.g. 170"
            className={inputClass}
          />
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <Input
              type="number"
              inputMode="numeric"
              value={heightFeet}
              onChange={(e) => {
                setHeightFeet(e.target.value);
                setError(null);
              }}
              placeholder="ft"
              className={inputClass}
            />
            <Input
              type="number"
              inputMode="decimal"
              step="0.1"
              value={heightInches}
              onChange={(e) => {
                setHeightInches(e.target.value);
                setError(null);
              }}
              placeholder="in"
              className={inputClass}
            />
          </div>
        )}
      </div>

      {/* Weight */}
      <div className="space-y-2 mb-4">
        <div className="flex items-center justify-between">
          <Label className={labelClass}>Weight</Label>
          <div className="flex gap-1">
            {(["kg", "st/lbs"] as const).map((u) => (
              <button
                key={u}
                type="button"
                onClick={() => handleWeightUnitSwitch(u)}
                className={`${pillBase} ${weightUnit === u ? pillActive : pillInactive}`}
              >
                {u}
              </button>
            ))}
          </div>
        </div>
        {weightUnit === "kg" ? (
          <Input
            type="number"
            inputMode="decimal"
            step="0.1"
            value={weightKgText}
            onChange={(e) => {
              setWeightKgText(e.target.value);
              setError(null);
            }}
            placeholder="e.g. 95"
            className={inputClass}
          />
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <Input
              type="number"
              inputMode="numeric"
              value={weightStone}
              onChange={(e) => {
                setWeightStone(e.target.value);
                setError(null);
              }}
              placeholder="st"
              className={inputClass}
            />
            <Input
              type="number"
              inputMode="decimal"
              step="0.1"
              value={weightPounds}
              onChange={(e) => {
                setWeightPounds(e.target.value);
                setError(null);
              }}
              placeholder="lbs"
              className={inputClass}
            />
          </div>
        )}
      </div>

      {/* Live BMI */}
      {bmi !== null && (
        <div
          className={`mb-4 px-4 py-3 rounded-lg text-center ${
            isDarkMode ? "bg-gray-700" : "bg-gray-100"
          }`}
        >
          <p
            className={`text-xs mb-0.5 ${
              isDarkMode ? "text-gray-400" : "text-gray-500"
            }`}
          >
            Calculated BMI
          </p>
          <p
            className={`text-2xl font-bold ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            {bmi}{" "}
            <span className="text-sm font-normal">kg/m²</span>
          </p>
        </div>
      )}

      {/* Inclusion threshold panel */}
      <div
        className={`mb-4 px-3 py-2 rounded-lg text-sm ${
          isDarkMode
            ? "bg-gray-700 text-gray-300"
            : "bg-gray-50 text-gray-600"
        }`}
      >
        <p className="font-medium mb-0.5">
          Inclusion threshold for this patient:
        </p>
        {isAdjusted ? (
          <>
            <p>BMI ≥27.5 kg/m²</p>
            <p
              className={`text-xs mt-0.5 ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              {(resolvedEthnicGroup ?? patientEthnicAdjustmentGroup ?? "")
                .replace(/_/g, " ")
                .replace(/\b\w/g, (c) => c.toUpperCase())}{" "}
              — adjusted threshold applies
            </p>
          </>
        ) : (
          <>
            <p>BMI ≥30 kg/m² (standard)</p>
            <p
              className={`text-xs mt-0.5 ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              Or ≥27 with ≥1 weight-related comorbidity
            </p>
          </>
        )}
      </div>

      {error && (
        <Alert className="mb-3 border-red-200 bg-red-50 text-red-900">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Button
        className="w-full"
        size="lg"
        onClick={handleContinue}
        disabled={!resolvedHeightCm || !resolvedWeightKg}
      >
        Save and continue
      </Button>
    </div>
  );
}
